import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { MARKETPLACE_AUTH_CONFIG } from './marketplace-auth.config';
import { MarketplaceAuthService } from './marketplace-auth.service';
import { MarketplaceTokenPersistenceService, PersistedAuthTokens } from './marketplace-token-persistence.service';
import { SauthTokenStore } from './sauth-token.store';
import { UserContextService } from './user-context.service';
import { AppState } from '../store';
import { AuthActions } from '../store/auth/auth.actions';
import { selectRefreshToken } from '../store/auth/auth.selectors';
import { decodeJwt, mapClaimsToUser, IdTokenClaims, TokenResponse } from '../utils/jwt.utils';

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Proactively refreshes the Delfi access/id/refresh tokens before they expire.
 * Schedules a timer 5 minutes before expiry and POSTs grant_type=refresh_token.
 * On failure, forces a full re-login (Delfi SSO typically completes silently).
 */
@Injectable({ providedIn: 'root' })
export class MarketplaceTokenRefreshService {
  private readonly http               = inject(HttpClient);
  private readonly store              = inject(Store<AppState>);
  private readonly authService        = inject(MarketplaceAuthService);
  private readonly persistence        = inject(MarketplaceTokenPersistenceService);
  private readonly sauthTokenStore    = inject(SauthTokenStore);
  private readonly userContextService = inject(UserContextService);

  private timerId: ReturnType<typeof setTimeout> | undefined;

  schedule(tokenExpiresAt: number): void {
    this.cancelTimer();
    const delay = Math.max(0, tokenExpiresAt * 1000 - REFRESH_BEFORE_EXPIRY_MS - Date.now());
    const expIso = new Date(tokenExpiresAt * 1000).toISOString();

    if (delay > 0) {
      console.log(`[TokenRefresh] Scheduled: exp=${expIso}, refresh in ${Math.round(delay / 60_000)}min`);
    } else {
      console.warn(`[TokenRefresh] Token expiring soon (exp=${expIso}) — refreshing immediately`);
    }

    this.timerId = setTimeout(() => this.doRefresh(), delay);
  }

  cancelTimer(): void {
    if (this.timerId !== undefined) {
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }

  private doRefresh(): void {
    console.log('[TokenRefresh] Refreshing token…');

    this.store.select(selectRefreshToken).pipe(take(1)).subscribe(storeToken => {
      // Fall back to persistence when store is empty — happens when restoring an expired
      // token on page load (tokensStored was never dispatched for the expired token).
      const refreshToken = storeToken ?? this.persistence.load()?.refreshToken ?? null;

      if (!refreshToken) {
        console.warn('[TokenRefresh] No refresh token in store or localStorage — forcing re-login');
        this.forceRelogin();
        return;
      }

      const body = new URLSearchParams({
        grant_type:    'refresh_token',
        refresh_token: refreshToken,
        client_id:     MARKETPLACE_AUTH_CONFIG.clientId,
        scope:         `openid ${MARKETPLACE_AUTH_CONFIG.serviceClientId}`,
      });

      this.http.post<TokenResponse>(MARKETPLACE_AUTH_CONFIG.tokenUrl, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }).pipe(take(1)).subscribe({
        next:  (tokens) => this.onRefreshSuccess(tokens),
        error: (err) => {
          console.error('[TokenRefresh] Refresh failed:', err?.status, err?.message);
          this.forceRelogin();
        },
      });
    });
  }

  private onRefreshSuccess(tokens: TokenResponse): void {
    if (!tokens.id_token) {
      console.warn('[TokenRefresh] Refresh response missing id_token — forcing re-login');
      this.forceRelogin();
      return;
    }

    let claims: IdTokenClaims;
    try {
      claims = decodeJwt(tokens.id_token);
    } catch {
      console.error('[TokenRefresh] Refresh id_token payload is malformed — forcing re-login');
      this.forceRelogin();
      return;
    }

    const persisted: PersistedAuthTokens = {
      accessToken:    tokens.access_token,
      idToken:        tokens.id_token,
      refreshToken:   tokens.refresh_token ?? null,
      tokenExpiresAt: claims.exp,
    };
    this.persistence.save(persisted);

    this.store.dispatch(AuthActions.tokensStored({
      accessToken:    tokens.access_token,
      idToken:        tokens.id_token,
      refreshToken:   tokens.refresh_token ?? null,
      tokenExpiresAt: claims.exp,
    }));
    this.store.dispatch(AuthActions.signInSuccess({ user: mapClaimsToUser(claims) }));

    this.sauthTokenStore.set(tokens.access_token);
    this.userContextService.load('token refresh');

    this.schedule(claims.exp);
  }

  private forceRelogin(): void {
    this.cancelTimer();
    this.persistence.clear();
    this.store.dispatch(AuthActions.signOut());
    this.authService.login();
  }
}
